/**
 * Breadcrumb Filter TypeScript
 * 
 * Google Drive-like breadcrumb navigation for folder filtering
 * in the media library with support for All Files, User Files, and Org Drive
 */

// Type declarations
declare var breadcrumbFilter: {
    ajaxUrl: string;
    nonce: string;
    currentDrive: string;
    currentFolder: number;
    strings: {
        allFiles: string;
        userFiles: string;
        orgDrive: string;
        loading: string;
    };
};

interface BreadcrumbItem {
    id: number;
    name: string;
    url: string;
    is_root?: boolean;
    is_current?: boolean;
}

// JQuery interface is provided by @types/jquery

(function($: any) {
    'use strict';

    class BreadcrumbFilter {
        private currentDrive: string;
        private currentFolder: number;
        private isLoading: boolean = false;
        private container: JQuery | null = null;
        private lastUpdateKey: string = '';
        private lastUpdateTime: number = 0;

        constructor() {
            this.currentDrive = breadcrumbFilter.currentDrive || 'media_gallery';
            this.currentFolder = breadcrumbFilter.currentFolder || 0;
            this.init();
        }

        /**
         * In admin drives backed by RML, "All files" / root is represented by rml_folder = -1.
         * Breadcrumbs use folderId=0 for root; we map it to -1 for RML/cookie state.
         */
        private isRmlBackedDrive(drive: string): boolean {
            return drive === 'user_files' || drive === 'my_files' || drive === 'org_drive';
        }

        private toRmlFolderId(drive: string, folderId: number): number {
            if (this.isRmlBackedDrive(drive) && (!folderId || folderId === 0)) {
                return -1;
            }
            return folderId;
        }

        private setCookie(name: string, value: string, days: number = 7): void {
            try {
                const expires = new Date(Date.now() + days * 864e5).toUTCString();
                document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
            } catch {
                // ignore
            }
        }

        private init(): void {
            this.renderBreadcrumb();
            this.bindEvents();
            this.positionBreadcrumb();
            
            // Listen for external updates
            this.bindExternalEvents();
            
            // Initialize RML monitoring
            this.initRMLMonitoring();
        }

        private renderBreadcrumb(): void {
            const template = document.getElementById('vm-breadcrumb-filter-template');
            if (!template) {
                return;
            }

            const targetElement = this.findInsertionPoint();
            if (targetElement) {
                $(targetElement).before(template.innerHTML);
                this.container = $('.vm-breadcrumb-filter-container');
            }
        }

        private findInsertionPoint(): Element | null {
            // Priority order for insertion points - should be before .media-toolbar.wp-filter
            const selectors = [
                '.media-toolbar.wp-filter', // Primary target
                '.wp-filter', // Fallback
                '.media-toolbar', // Another fallback
                '.tablenav.top', // List view
                '.media-frame-content .attachments-browser', // Grid view
                '.wrap h1' // Last resort
            ];

            for (const selector of selectors) {
                const element = document.querySelector(selector);
                if (element) {
                    return element;
                }
            }

            return null;
        }

        private positionBreadcrumb(): void {
            // Ensure breadcrumb is positioned correctly
            setTimeout(() => {
                this.adjustBreadcrumbPosition();
            }, 100);
        }

        private adjustBreadcrumbPosition(): void {
            if (!this.container) return;

            // Check if we need to move the breadcrumb
            const mediaToolbar = document.querySelector('.media-toolbar.wp-filter');
            const wpFilter = document.querySelector('.wp-filter');
            
            if (mediaToolbar && !this.isElementBefore((this.container as any)[0], mediaToolbar)) {
                $(this.container).insertBefore(mediaToolbar);
            } else if (wpFilter && !this.isElementBefore((this.container as any)[0], wpFilter)) {
                $(this.container).insertBefore(wpFilter);
            }
        }

        private isElementBefore(element1: Element, element2: Element): boolean {
            return !!(element2.compareDocumentPosition(element1) & Node.DOCUMENT_POSITION_PRECEDING);
        }

        private bindEvents(): void {
            // Handle breadcrumb clicks - only on upload.php pages
            $(document).on('click', '.vm-breadcrumb-link', (e: any) => {
                // Check if we're on a drive widget page - if so, let VMDriveWidget handle it
                if (document.querySelector('.vm-drive-widget')) {
                    return;
                }
                
                e.preventDefault();
                e.stopPropagation();
                this.handleBreadcrumbClick(e.currentTarget);
            });

            // Listen for DOM changes that might affect positioning
            const observer = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    if (mutation.type === 'childList') {
                        mutation.addedNodes.forEach((node: any) => {
                            if (node.nodeType === Node.ELEMENT_NODE) {
                                if (node.classList?.contains('media-toolbar') || 
                                    node.classList?.contains('wp-filter')) {
                                    this.adjustBreadcrumbPosition();
                                }
                            }
                        });
                    }
                });
            });

            observer.observe(document.body, {
                childList: true,
                subtree: true
            });
        }

        private bindExternalEvents(): void {
            // Listen for folder changes from RML or custom folder selector
            $(document).on('rml-folder-changed', (e: any, folderId: number) => {
                this.updateBreadcrumb(this.currentDrive, folderId);
            });

            // Listen for custom folder selector changes
            $(document).on('custom-folder-selected', (e: any, data: any) => {
                this.updateBreadcrumb(this.currentDrive, data.folderId || 0);
            });

            // Listen for drive changes from media library tabs
            $(document).on('media-library-tab-changed', (e: any, drive: string) => {
                this.currentDrive = drive;
                this.updateBreadcrumb(drive, 0); // Reset to root when changing drives
            });

            // Listen for RML tree selection changes
            $(document).on('rml:folder:change', (e: any, folderId: number) => {
                this.updateBreadcrumb(this.currentDrive, folderId || 0);
            });

            // Listen for RML tree updates
            $(document).on('rml:tree:update', (e: any) => {
                // Refresh breadcrumb after tree updates
                setTimeout(() => {
                    const currentFolder = this.getCurrentFolderFromURL();
                    this.updateBreadcrumb(this.currentDrive, currentFolder);
                }, 100);
            });

            // Listen for more RML events
            $(document).on('rml:folder:selected rml:folder:clicked', (e: any, data: any) => {
                const folderId = (typeof data === 'number') ? data : (data && data.id ? data.id : 0);
                if (folderId !== this.currentFolder) {
                    this.updateBreadcrumb(this.currentDrive, folderId);
                }
            });

            // Listen for URL changes (for RML navigation)
            let lastUrl = location.href;
            new MutationObserver(() => {
                const url = location.href;
                if (url !== lastUrl) {
                    lastUrl = url;
                    const currentFolder = this.getCurrentFolderFromURL();
                    this.updateBreadcrumb(this.currentDrive, currentFolder);
                }
            }).observe(document, { subtree: true, childList: true });

            // Listen for page visibility changes
            document.addEventListener('visibilitychange', () => {
                if (!document.hidden && this.container) {
                    setTimeout(() => {
                        this.adjustBreadcrumbPosition();
                    }, 100);
                }
            });

            // Listen for popstate (back/forward navigation)
            window.addEventListener('popstate', () => {
                setTimeout(() => {
                    const currentFolder = this.getCurrentFolderFromURL();
                    this.updateBreadcrumb(this.currentDrive, currentFolder);
                }, 100);
            });

            // Manual refresh mechanism - check every few seconds
            setInterval(() => {
                if (!this.container) {
                    this.renderBreadcrumb();
                }
            }, 3000);
        }

        private handleBreadcrumbClick(link: HTMLElement): void {
            if (this.isLoading) return;

            const folderId = parseInt($(link).data('folder-id')) || 0;
            const drive = $(link).data('drive') || this.currentDrive;

            this.navigateToFolder(drive, folderId);
        }

        private navigateToFolder(drive: string, folderId: number): void {
            // Update current state immediately
            this.currentFolder = folderId;
            this.currentDrive = drive;

            // IMPORTANT: when user clicks "All files" (root) in RML-backed drives,
            // clear the persisted folder selection so other components don't restore the last folder.
            if (folderId === 0 && this.isRmlBackedDrive(drive)) {
                this.setCookie('rml_folder', '-1', 7);
                try {
                    const w = window as any;
                    if (w.vmRMLFolderTracker && typeof w.vmRMLFolderTracker.clearFolder === 'function') {
                        w.vmRMLFolderTracker.clearFolder();
                    }
                } catch {}
            }

            // Update external components BEFORE triggering events
            this.updateExternalComponents(folderId);

            // Trigger custom event for other components
            this.triggerNavigationEvent(drive, folderId);

            // Update the media library content via AJAX instead of page refresh
            this.updateMediaLibraryContent(drive, folderId);

            // Update breadcrumb display
            this.updateBreadcrumb(drive, folderId);
        }

        private triggerNavigationEvent(drive: string, folderId: number): void {
            $(document).trigger('breadcrumb-folder-navigate', {
                folderId: folderId,
                drive: drive,
                isRoot: folderId === 0
            });
        }

        private resetToRoot(drive: string): void {
            this.isLoading = true;
            this.setLoadingState(true);

            // Reset custom folder selector if present
            this.resetCustomFolderSelector();

            // Reset RML if present
            this.resetRMLSelection();

            // Navigate to root
            const rootUrl = this.buildFolderUrl(drive, 0);
            window.location.href = rootUrl;
        }


        private navigateToFolderAjax(drive: string, folderId: number): Promise<any> {
            return new Promise((resolve, reject) => {
                $.post(breadcrumbFilter.ajaxUrl, {
                    action: 'navigate_breadcrumb',
                    drive: drive,
                    folder_id: folderId,
                    nonce: breadcrumbFilter.nonce
                })
                .done((response: any) => {
                    if (response.success) {
                        resolve(response.data);
                    } else {
                        reject(new Error(response.data || 'Navigation failed'));
                    }
                })
                .fail((xhr: any, status: string, error: string) => {
                    reject(new Error(error));
                });
            });
        }

        private updateBreadcrumb(drive: string, folderId: number): void {
            if (this.shouldSkipUpdate()) {
                return;
            }

            this.prepareForUpdate(drive, folderId);
            this.fetchBreadcrumbData(drive, folderId);
        }

        private shouldSkipUpdate(): boolean {
            const timeDiff = Date.now() - this.lastUpdateTime;
            return this.isLoading || timeDiff < 1000;
        }

        private prepareForUpdate(drive: string, folderId: number): void {
            this.isLoading = true;
            this.lastUpdateTime = Date.now();
            this.currentDrive = drive;
            this.currentFolder = folderId;
        }

        private fetchBreadcrumbData(drive: string, folderId: number): void {
            const payload: any = {
                action: 'get_folder_breadcrumb',
                drive: drive,
                folder_id: folderId,
                nonce: breadcrumbFilter.nonce
            };

            // In user_files drive, breadcrumbs must respect the *selected user* (admin managing other users).
            // Send custom_user_id so PHP can validate visibility correctly.
            if (drive === 'user_files') {
                const selectedUserIdRaw = ($('#user-folder-select') as any).val();
                const selectedUserId = parseInt(String(selectedUserIdRaw || ''), 10);
                if (selectedUserId && selectedUserId > 0) {
                    payload.custom_user_id = selectedUserId;
                }
            }

            $.post(breadcrumbFilter.ajaxUrl, payload)
            .done((response: any) => this.handleBreadcrumbResponse(response))
            .fail(() => {
                // Silent fail - breadcrumb update is not critical
            })
            .always(() => {
                this.isLoading = false;
            });
        }

        private handleBreadcrumbResponse(response: any): void {
            if (!response.success || !this.container) {
                return;
            }

            const breadcrumbList = this.container.find('.vm-breadcrumb-list');
            if (breadcrumbList.length && response.data.html) {
                breadcrumbList.html(response.data.html);
            }
        }

        private resetCustomFolderSelector(): void {
            // Reset custom folder selector if present
            const customSelector = $('.custom-folder-selector');
            if (customSelector.length && (customSelector as any).select2) {
                (customSelector as any).select2('val', '');
                customSelector.trigger('change');
            }
        }

        private resetRMLSelection(): void {
            // Reset RML tree selection if present
            if (typeof (window as any).wp !== 'undefined' && (window as any).wp.rml) {
                // Trigger RML reset if available
                $(document).trigger('rml-reset-selection');
            }
        }

        private updateExternalComponents(folderId: number): void {
            this.applyDriveBasedFolderFiltering();
            // NOTE: user_files uses a *user* selector (#user-folder-select), not a folder selector.
            // Do not attempt to write folderId into Select2 there; it causes invalid state resets.
            this.updateCustomFolderSelector(folderId);
            this.updateRMLSelection(folderId);
            // Keep URLs stable (drive-only) for AJAX-driven admin media library drives.
            // WordPress/RML already mutate history; adding rml_folder here fights other components.
            this.updateBrowserUrl(folderId);
        }

        private updateCustomFolderSelector(folderId: number): void {
            // Legacy: some pages used a folder selector. On upload.php?drive=user_files we must not do this.
            const drive = this.currentDrive;
            if (drive === 'user_files') {
                return;
            }
        }

        private updateRMLSelection(folderId: number): void {
            const rmlFolderId = this.toRmlFolderId(this.currentDrive, folderId);
            this.syncRmlTreeHighlight(rmlFolderId);
            $(document).trigger('rml-folder-select', [rmlFolderId]);
        }

        /**
         * Keep sidebar tree highlight in sync with breadcrumb navigation.
         */
        private syncRmlTreeHighlight(rmlFolderId: number): void {
            try {
                const hooks: any = (window as any).rml?.hooks;
                if (hooks && typeof hooks.call === 'function') {
                    hooks.call('tree/select', rmlFolderId > 0 ? rmlFolderId : -1);
                }
            } catch {
                // ignore
            }

            $('.rml-container [data-id], .rml-container [data-rml-id]')
                .removeClass('aiot-active aio-node-active selected ant-tree-node-selected aio-node-selected');
            $('.rml-container .ant-tree-treenode').removeClass('ant-tree-treenode-selected');

            if (rmlFolderId > 0) {
                const selectors = [
                    `.rml-container [data-id="${rmlFolderId}"]`,
                    `.rml-container [data-rml-id="${rmlFolderId}"]`,
                ];
                for (const selector of selectors) {
                    const $node = $(selector).first();
                    if ($node.length) {
                        $node.addClass('aiot-active selected ant-tree-node-selected aio-node-selected');
                        if ($node.hasClass('ant-tree-treenode')) {
                            $node.addClass('ant-tree-treenode-selected');
                        }
                        break;
                    }
                }
            }
        }

        private updateBrowserUrl(folderId: number): void {
            // For AJAX-only admin drives, don't push rml_folder into URL.
            // It triggers server-side "clean URL" redirects and breaks folder state.
            if (this.currentDrive === 'user_files' || this.currentDrive === 'my_files' || this.currentDrive === 'org_drive') {
                return;
            }
            const newUrl = this.buildFolderUrl(this.currentDrive, folderId);
            if (window.history?.pushState) {
                window.history.pushState({ drive: this.currentDrive, folder: folderId }, '', newUrl);
            }
        }

        private updateMediaLibraryContent(drive: string, folderId: number): void {
            if (this.isUserFilesRoot(drive, folderId)) {
                this.triggerEmptyMediaState();
                return;
            }
            
            this.updateWordPressMediaLibrary(drive, folderId);
            this.triggerMediaLibraryEvents(drive, folderId);
        }

        private isUserFilesRoot(drive: string, folderId: number): boolean {
            return drive === 'user_files' && folderId === 0;
        }

        private triggerEmptyMediaState(): void {
            setTimeout(() => {
                if (typeof (window as any).initializeEmptyMediaState === 'function') {
                    (window as any).initializeEmptyMediaState();
                }
            }, 100);
        }

        private updateWordPressMediaLibrary(drive: string, folderId: number): void {
            const wp = (window as any).wp;
            if (!wp?.media) return;

            const frame = wp.media.frame;
            const library = frame?.content?.get()?.collection;
            
            if (library) {
                library.props.set({
                    rml_folder: this.toRmlFolderId(drive, folderId),
                    drive: drive
                });
                library.more();
            }
        }

        private triggerMediaLibraryEvents(drive: string, folderId: number): void {
            $(document).trigger('media-library-refresh', { drive, folder: folderId });
            
            if ($('.attachments-browser').length) {
                $(document).trigger('attachments:refresh');
            }
        }

        /**
         * Apply drive-based folder filtering to RML tree
         * Uses the same logic as custom-folder-selector
         */
        private applyDriveBasedFolderFiltering(): void {
            switch (this.currentDrive) {
                case 'user_files':
                    this.handleUserFilesDriveFiltering();
                    break;
                case 'org_drive':
                    this.showOrgRMLFolders();
                    break;
                case 'shared':
                    // Hide tree; attachments pane will show accessible items only
                    this.hideAllRMLFolders();
                    break;
                case 'media_gallery':
                    this.showAllRMLFolders();
                    break;
            }
        }

        private handleUserFilesDriveFiltering(): void {
            if (this.currentFolder === 0) {
                this.handleUserFilesRoot();
                return;
            }

            // In user_files, the selector is the user picker (#user-folder-select)
            const selectedUserId = ($('#user-folder-select') as any).val();
            
            if (selectedUserId && selectedUserId !== '0') {
                this.filterRMLTreeByUser(parseInt(selectedUserId.toString()));
            } else {
                this.hideAllRMLFolders();
            }
        }

        private handleUserFilesRoot(): void {
            this.hideAllRMLFolders();
            setTimeout(() => {
                if (typeof (window as any).initializeEmptyMediaState === 'function') {
                    (window as any).initializeEmptyMediaState();
                }
            }, 100);
        }

        /**
         * Filter RML tree to show only user folders
         * Reuses logic from custom-folder-selector
         */
        private filterRMLTreeByUser(userId: number): void {
            // Trigger the same filtering used by custom-folder-selector
            $(document).trigger('filter-rml-by-user', [userId]);
            
            // Fallback: call the global function if available
            if (typeof (window as any).filterRMLTreeByUser === 'function') {
                (window as any).filterRMLTreeByUser(userId);
            }
        }

        /**
         * Hide all RML folders
         */
        private hideAllRMLFolders(): void {
            // Use the same logic as custom-folder-selector
            if (typeof (window as any).hideAllRMLFoldersOnLoad === 'function') {
                (window as any).hideAllRMLFoldersOnLoad();
            }
            // Also call the regular hideAllRMLFolders if available
            if (typeof (window as any).hideAllRMLFolders === 'function') {
                (window as any).hideAllRMLFolders();
            }
        }

        /**
         * Show all RML folders
         */
        private showAllRMLFolders(): void {
            // Use the same logic as custom-folder-selector
            if (typeof (window as any).showAllRMLFolders === 'function') {
                (window as any).showAllRMLFolders();
            }
        }

        /**
         * Show organization folders (non-user folders)
         */
        private showOrgRMLFolders(): void {
            // Show only folders that have _org_id meta (organization folders)
            // Hide user folders and folders without org assignment
            const $folders = $('.rml-folder');
            
            $folders.each((index: number, folder: HTMLElement) => {
                const $folder = $(folder);
                const folderId = $folder.data('id');
                
                if (folderId) {
                    // Check if this folder has org_id meta via AJAX or hide by default
                    // For now, we'll need to implement org folder detection
                    this.checkIfOrgFolder(folderId, $folder);
                } else {
                    $folder.hide();
                }
            });
        }

        private checkIfOrgFolder(folderId: number, $folder: JQuery): void {
            // Check if this folder has _org_id meta via AJAX
            const ajaxUrl = (window as any).ajaxurl || '/wp-admin/admin-ajax.php';
            $.post(ajaxUrl, {
                action: 'check_org_folder',
                folder_id: folderId,
                nonce: (window as any).breadcrumbFilter?.nonce || ''
            }).done((response: any) => {
                if (response.success && response.data.is_org_folder) {
                    ($folder as any).show();
                } else {
                    ($folder as any).hide();
                }
            }).fail(() => {
                // On error, hide the folder
                ($folder as any).hide();
            });
        }

        private buildFolderUrl(drive: string, folderId: number = 0): string {
            let url = `upload.php?drive=${drive}`;
            
            if (folderId > 0) {
                url += `&rml_folder=${folderId}`;
            }

            // Add current page parameters if needed
            const urlParams = new URLSearchParams(window.location.search);
            const mode = urlParams.get('mode');
            if (mode) {
                url += `&mode=${mode}`;
            }

            return url;
        }

        private getCurrentFolderFromURL(): number {
            const urlParams = new URLSearchParams(window.location.search);
            const rmlFolder = urlParams.get('rml_folder');
            const folderId = urlParams.get('folder_id');
            
            if (rmlFolder) {
                return parseInt(rmlFolder) || 0;
            }
            if (folderId) {
                return parseInt(folderId) || 0;
            }

            // IMPORTANT (Option A):
            // Do NOT let folder state bleed across drives.
            // Only fall back to cookie/tracker for drives that actually require folder context.
            const drivesRequiringFolder = ['org_drive', 'user_files', 'my_files'];
            if (drivesRequiringFolder.indexOf(this.currentDrive) === -1) {
                return 0;
            }

            // AJAX-only admin drives (org_drive/user_files/my_files) often do NOT keep rml_folder in the URL.
            // Fall back to RML cookie (authoritative) and our global tracker only for those drives.
            try {
                const match = document.cookie.match(/(?:^|;\s*)rml_folder=([^;]+)/);
                if (match && match[1]) {
                    const decoded = decodeURIComponent(match[1]);
                    const c = parseInt(decoded, 10);
                    if (c === -1) {
                        return 0; // "All files" / Unorganized bucket => treat as root for breadcrumb
                    }
                    if (!Number.isNaN(c) && c > 0) {
                        return c;
                    }
                }
            } catch {}

            try {
                const w: any = window as any;
                if (w.vmCurrentRMLFolder && Number(w.vmCurrentRMLFolder) > 0) {
                    return Number(w.vmCurrentRMLFolder);
                }
            } catch {}

            return 0;
        }

        private initRMLMonitoring(): void {
            this.setupRMLTreeMonitoring();
            this.setupDOMObservers();
            this.setupEventListeners();
        }

        private setupRMLTreeMonitoring(): void {
            const checkRMLTree = () => {
                const rmlTree = this.findRMLTree();
                if (rmlTree) {
                    this.attachRMLTreeClickHandlers(rmlTree);
                }
            };

            checkRMLTree();
            this.rmlTreeChecker = checkRMLTree;
        }

        private findRMLTree(): Element | null {
            const selectors = [
                '.rml-container .aio-tree',
                '.rml-container',
                '.rml-tree',
                '.aio-tree',
                '[data-rml-tree]'
            ];

            for (const selector of selectors) {
                const element = document.querySelector(selector);
                if (element) return element;
            }
            return null;
        }

        private attachRMLTreeClickHandlers(rmlTree: Element): void {
            const itemSelectors = [
                '.aio-tree-item', 
                '.rml-tree-item', 
                '[data-id]', 
                '[data-li-id]',  // Add support for RML tree special items
                '.wp-list-table tr'
            ];
            
            itemSelectors.forEach(selector => {
                $(rmlTree).on('click', selector, (e: any) => {
                    // Do NOT stopPropagation here; other scripts (RML, tracker, custom selector) need the click.
                    this.handleRMLTreeItemClick(e.currentTarget, e.target);
                });
            });
        }

        private handleRMLTreeItemClick(clickedElement: Element, rawTarget?: Element): void {
            // Prevent duplicate executions by checking if we're already processing
            if (this.isLoading) return;

            // Resolve folder id from the *closest* node that actually holds ids (fixes clicks on inner spans).
            const targetEl = (rawTarget as any) as Element | undefined;
            const holder = (targetEl?.closest?.('[data-id],[data-rml-id],[data-li-id]') ||
                (clickedElement as any)?.closest?.('[data-id],[data-rml-id],[data-li-id]') ||
                clickedElement) as Element;

            const special = this.checkForSpecialRMLItems(holder);
            const folderId = special !== null ? special : this.extractFolderIdFromElement(holder);
            const isSpecialFolder = special !== null;
            
            setTimeout(() => {
                const urlFolder = this.getCurrentFolderFromURL();
                // If we couldn't determine a folder, do nothing (do NOT reset to 0).
                if (typeof folderId !== 'number' || Number.isNaN(folderId)) {
                    return;
                }
                const finalFolderId = folderId;
                
                // Update breadcrumb if folder changed OR if it's a special folder (to ensure proper root state)
                if (finalFolderId !== this.currentFolder || isSpecialFolder) {
                    this.currentFolder = finalFolderId;
                    this.updateBreadcrumb(this.currentDrive, finalFolderId);
                }
            }, 200);
        }

        private extractFolderIdFromElement(element: Element): number {
            // Check for special RML tree items first (All files, Unorganized)
            const specialFolderId = this.checkForSpecialRMLItems(element);
            if (specialFolderId !== null) {
                return specialFolderId;
            }

            // Prefer closest holder if we're on an inner element
            const holder = (element as any).closest?.('[data-id],[data-rml-id]') as Element | null;
            if (holder && holder !== element) {
                return this.extractFolderIdFromElement(holder);
            }

            // Try dataset first
            if ((element as any).dataset?.id) {
                return parseInt((element as any).dataset.id) || 0;
            }
            if ((element as any).dataset?.rmlId) {
                return parseInt((element as any).dataset.rmlId) || 0;
            }

            // Try getAttribute
            const dataId = element.getAttribute('data-id');
            if (dataId) {
                return parseInt(dataId) || 0;
            }
            const dataRmlId = element.getAttribute('data-rml-id');
            if (dataRmlId) {
                return parseInt(dataRmlId) || 0;
            }

            // Try child elements
            const childWithDataId = element.querySelector('[data-id]');
            if (childWithDataId) {
                const childDataId = childWithDataId.getAttribute('data-id');
                return parseInt(childDataId || '0') || 0;
            }

            return 0;
        }

        private checkForSpecialRMLItems(element: Element): number | null {
            // Check for data-li-id attribute (RML tree specific)
            const liId = element.getAttribute('data-li-id') || 
                        (element as any).dataset?.liId;
            
            if (liId) {
                // VM convention: -1 = All Files. RML "all" / legacy unorganized map to All Files on folder-scoped drives.
                if (liId === 'all' || liId === '-1' || liId === 'unorganized') return -1;
            }

            // Check parent elements for data-li-id
            const parentWithLiId = element.closest('[data-li-id]');
            if (parentWithLiId) {
                const parentLiId = parentWithLiId.getAttribute('data-li-id');
                if (parentLiId === 'all' || parentLiId === '-1' || parentLiId === 'unorganized') return -1;
            }

            return null; // Not a special item
        }

        private setupDOMObservers(): void {
            // Observer for RML container additions
            const rmlObserver = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    if (mutation.type === 'childList') {
                        mutation.addedNodes.forEach((node: any) => {
                            if (this.isRMLContainer(node)) {
                                setTimeout(this.rmlTreeChecker, 100);
                            }
                        });
                    }
                });
            });

            rmlObserver.observe(document.body, { childList: true, subtree: true });

            // Observer for media library changes
            this.setupMediaLibraryObserver();
        }

        private isRMLContainer(node: any): boolean {
            return node.nodeType === Node.ELEMENT_NODE && 
                   (node.classList?.contains('rml-container') || 
                    node.querySelector?.('.rml-container'));
        }

        private setupMediaLibraryObserver(): void {
            const mediaObserver = new MutationObserver((mutations) => {
                if (this.shouldCheckForFolderChanges(mutations)) {
                    setTimeout(() => {
                        const currentFolder = this.getCurrentFolderFromURL();
                        if (currentFolder !== this.currentFolder) {
                            this.currentFolder = currentFolder;
                            this.updateBreadcrumb(this.currentDrive, currentFolder);
                        }
                    }, 300);
                }
            });

            mediaObserver.observe(document.body, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ['class', 'data-id', 'data-rml-folder']
            });
        }

        private shouldCheckForFolderChanges(mutations: MutationRecord[]): boolean {
            return mutations.some(mutation => {
                if (mutation.type !== 'childList' && mutation.type !== 'attributes') {
                    return false;
                }

                const target = mutation.target as Element;
                return target && (
                    target.classList?.contains('attachments-browser') ||
                    target.classList?.contains('media-frame-content') ||
                    target.querySelector?.('.attachments-browser') ||
                    target.closest?.('.media-frame-content')
                );
            });
        }

        private setupEventListeners(): void {
            $(document).on('rml:ready rml:tree:ready', () => {
                setTimeout(this.rmlTreeChecker, 100);
            });
        }

        private rmlTreeChecker: () => void = () => {};

        private setLoadingState(isLoading: boolean): void {
            if (!this.container) return;

            if (isLoading) {
                this.container.addClass('vm-breadcrumb-loading');
                this.container.find('.vm-breadcrumb-link').addClass('disabled');
            } else {
                this.container.removeClass('vm-breadcrumb-loading');
                this.container.find('.vm-breadcrumb-link').removeClass('disabled');
            }
        }

        // Public methods for external integration
        public refresh(): void {
            this.positionBreadcrumb();
        }

        public updateFolder(drive: string, folderId: number): void {
            this.updateBreadcrumb(drive, folderId);
        }

        public getCurrentPath(): { drive: string, folder: number } {
            return {
                drive: this.currentDrive,
                folder: this.currentFolder
            };
        }
    }

    // Initialize when document is ready
    $(document).ready(() => {
        if (isUploadPage()) {
            initializeBreadcrumbFilter();
        }
    });

    function isUploadPage(): boolean {
        return window.location.pathname.includes('upload.php') || 
               document.body.classList.contains('upload-php');
    }

    function initializeBreadcrumbFilter(): void {
        const instance = new BreadcrumbFilter();
        (window as any).breadcrumbFilterInstance = instance;
        
        // Make methods globally available
        (window as any).refreshBreadcrumbFilter = () => {
            instance.refresh();
        };

        (window as any).updateBreadcrumbFilter = (drive: string, folderId: number) => {
            instance.updateFolder(drive, folderId);
        };
    }

})((window as any).jQuery);
